import { Caption, CaptionBold, Skeleton } from '@comeon/ui';
import { Link } from '~/utils/navigation';
import styled, { css } from 'styled-components';

type LabelProps = {
	isHighlighted?: boolean;
	size?: 'small' | 'large';
};

const getSizeStyles = ({ size }: LabelProps) => {
	if (size === 'large') {
		return css`
			font: var(--label-bold);
		`;
	}
	return null;
};

export const ScoreLabel = styled(CaptionBold)<LabelProps>`
	${getSizeStyles}
	color: ${({ isHighlighted }) =>
		isHighlighted
			? 'var(--sportsbook-event-scoreboard-score-color, rgba(var(--base-02), 1))'
			: 'var(--sportsbook-text-supportive)'};
	text-align: center;
`;

export const OngoingScoreLabel = styled(ScoreLabel)`
	padding: 0 4px;
	border-radius: 3px;

	background-color: var(--main-surface-2);
	color: var(--sportsbook-live-color, rgba(var(--base-02), 1));
`;

export const ServeIndicator = styled.span`
	display: flex;
	min-width: 6px;
	width: 6px;
	min-height: 6px;
	height: 6px;
	border-radius: 50%;
	margin: 0 8px;

	background-color: var(--sportsbook-event-scoreboard-serve-indicator, rgba(var(--primary), 1));
`;

export const ScoreboardParticipants = styled.div`
	overflow-x: hidden;
`;

export const ScoreGrid = styled.div<{ columnCount?: number; isDisplayingSetScores?: boolean }>`
	display: grid;
	grid-template-columns: repeat(${({ columnCount }) => columnCount}, minmax(auto, max-content));
	column-gap: 6px;
	row-gap: 4px;

	p + p {
		margin-top: 0;
	}

	${({ isDisplayingSetScores }) =>
		isDisplayingSetScores &&
		css`
			align-content: initial;
		`}
`;

export const ScoreRow = styled.div<{ row: number }>`
	display: contents;
	& > * {
		grid-row: ${({ row }) => row};
	}
`;

export const ParticipantAndServeIndicator = styled.div`
	display: flex;
	align-items: center;
	flex: 1 1 auto;
	overflow: hidden;
`;

export const ParticipantLabel = styled(Caption)<LabelProps>`
	flex-shrink: 1;
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
	${getSizeStyles}
	font-weight: ${({ isHighlighted }) => (isHighlighted ? 700 : 400)};
	color: var(--sportsbook-event-scoreboard-participant-color, rgba(var(--base-02), 1));
`;

export const EventNameLabel = styled(Caption)`
	color: var(--sportsbook-event-scoreboard-participant-color, rgba(var(--base-02), 1));
`;

export const WrapperLink = styled(Link)`
	padding-right: 10px;

	@media (hover: hover) and (pointer: fine) {
		&:hover {
			${ParticipantLabel}, ${EventNameLabel} {
				color: var(--main-text-primary);
			}
		}
	}
`;

export const ScoreboardSkeleton = ({ className = '' }) => (
	<div className={className}>
		<Skeleton width="140px" height="16px" margin="0 0 4px 0" />
		<Skeleton width="110px" height="16px" />
	</div>
);
